/**
 * Automatic sign-in: someone who already has a Microsoft session is signed in without
 * clicking anything — a full-page `loginRedirect`, which Microsoft completes on its own
 * when there is one account to use. Not `ssoSilent`: it frames this page, which
 * X-Frame-Options: DENY blocks, so it only added a 10-second wait (2026-09-30).
 *
 * Two guards keep it from ever looping or fighting the user, both in sessionStorage
 * (this tab only, survives the round trip to Microsoft and back):
 *
 *   ATTEMPTED   set just before the automatic redirect. If the page comes back with no
 *               account, the redirect did not work (cancelled, error, wrong account) —
 *               show the sign-in button and the reason instead of redirecting again.
 *               Cleared once someone is signed in.
 *   SIGNED_OUT  set by Sign out. Someone who just signed out sees the button, not an
 *               immediate trip back to Microsoft. Cleared by the button.
 *
 * Nothing automatic runs inside an iframe: MSAL's own silent sign-in loads this page
 * in a hidden one, and that copy must neither sign in nor touch the response.
 *
 * The same file lives in fcc-portal and cost-per-claim; keep them in step.
 */

export const ATTEMPTED_KEY = 'fcc.autoSignIn.attempted'
export const SIGNED_OUT_KEY = 'fcc.autoSignIn.signedOut'
/** Set when a stale saved sign-in has been sent to Microsoft once; cleared once a token comes back. */
export const RENEW_KEY = 'fcc.autoSignIn.renewTried'

/**
 * MSAL errors that mean "renew interactively", beyond InteractionRequiredAuthError.
 *
 * With the sign-in cache in localStorage (#192), a returning user has a saved account whose
 * refresh token may have expired. acquireTokenSilent then falls back to a hidden iframe,
 * which this app's X-Frame-Options: DENY refuses, so it fails with `monitor_window_timeout`
 * (a BrowserAuthError) after ~10s. That used to surface as "Sign-in unavailable"; it should
 * be a redirect, which comes straight back with no click when a Microsoft session exists.
 */
const RENEW_CODES = new Set([
  'monitor_window_timeout',
  'block_iframe_reload',
  'iframe_closed_prematurely',
  'silent_sso_error',
  'login_required',
  'consent_required',
  'interaction_required',
  'no_tokens_found',
  'refresh_token_expired',
])

export function needsInteractiveRenewal(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false
  const err = e as { name?: string; errorCode?: string }
  if (err.name === 'InteractionRequiredAuthError') return true
  return typeof err.errorCode === 'string' && RENEW_CODES.has(err.errorCode)
}

export interface AutoSignInState {
  signedIn: boolean
  /** An automatic redirect already ran in this tab and came back without an account. */
  attempted: boolean
  /** The user signed out in this tab. */
  signedOut: boolean
  inIframe: boolean
  /** sessionStorage works. Without it there is no loop guard, so nothing automatic. */
  storageOk: boolean
}

/**
 * - `none`    signed in, or in an iframe: do nothing.
 * - `try`     loginRedirect.
 * - `manual`  show the sign-in button.
 */
export type AutoSignInStep = 'none' | 'try' | 'manual'

export function autoSignInStep(s: AutoSignInState): AutoSignInStep {
  if (s.signedIn || s.inIframe) return 'none'
  if (!s.storageOk || s.signedOut || s.attempted) return 'manual'
  return 'try'
}

/**
 * True when this page is MSAL's hidden silent-sign-in frame coming back with the
 * response in its hash. The parent window reads that hash; this copy must not start
 * MSAL at all, or it can consume the response first.
 */
export function isSilentFrameResponse(inIframe: boolean, hash: string): boolean {
  return inIframe && /(^|[#&])(code|error)=/.test(hash)
}

export function inIframe(): boolean {
  try {
    return window.self !== window.top
  } catch {
    // Cross-origin parent: reading `top` throws, which itself means framed.
    return true
  }
}

export const flags = {
  ok(): boolean {
    try {
      const k = 'fcc.autoSignIn.probe'
      sessionStorage.setItem(k, '1')
      sessionStorage.removeItem(k)
      return true
    } catch {
      return false
    }
  },
  get(key: string): boolean {
    try {
      return sessionStorage.getItem(key) === '1'
    } catch {
      return false
    }
  },
  set(key: string): void {
    try {
      sessionStorage.setItem(key, '1')
    } catch {
      /* no storage: autoSignInStep already refuses to redirect */
    }
  },
  clear(key: string): void {
    try {
      sessionStorage.removeItem(key)
    } catch {
      /* nothing to clear */
    }
  },
}

/** The notice for the sign-in card after an automatic attempt came back empty. */
export const AUTO_FAILED_NOTICE =
  'Automatic sign-in did not complete. Sign in with Microsoft to continue.'
